import React, { memo, useCallback, useEffect, useState, Fragment } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Checkbox from '@material-ui/core/Checkbox'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import Input from '@material-ui/core/Input'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import { ConfirmDelete } from '../confirmation/Confirm'
import AreaSelect from '../area/AreaSelect'
import LOG from '../../logging/Logger'
import {
  MAX_LENGTH_PROJECT_DESCRIPTION,
  MAX_LENGTH_PROJECT_NAME
} from '../../data/limits'

const useStyles = makeStyles(theme => ({
  description: {
    whiteSpace: 'pre-line',
    margin: theme.spacing(1)
  },
  accordion: {
    marginBottom: theme.spacing(1)
  },
  accordionDetails: {
    padding: theme.spacing(1, 0.5, 1, 0.5)
  },
  checkbox: {
    padding: theme.spacing(0, 1, 0, 1)
  },
  fullWidth: {
    width: 'calc(90%)'
  }
}))

const newProjectName = 'New Project'
const emptyDescription = 'Add Project Description'

export default memo(
  ({
    project,
    onDeleteProject,
    onUpdateProject,
    onEditMode,
    getAreas,
    decrypt
  }) => {
    LOG.debug('Render: ProjectTitle')

    const classes = useStyles()
    const theme = useTheme()

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')

    const [editName, setEditName] = useState(false)
    const [editDescription, setEditDescription] = useState(false)

    const [autoPromote, setAutoPromote] = useState(false)
    const [autoDelete, setAutoDelete] = useState(false)

    const [openConfirmDeleteProject, setOpenConfirmDeleteProject] = useState(
      false
    )

    useEffect(() => {
      setName(decrypt(project.name))
      setDescription(decrypt(project.description) || emptyDescription)
      setAutoPromote(project.autoPromote === true)
      setAutoDelete(project.autoDelete === true)
      setEditName(decrypt(project.name) === newProjectName)
    }, [
      decrypt,
      project.autoDelete,
      project.autoPromote,
      project.description,
      project.name
    ])

    const handleSave = useCallback(() => {
      LOG.info('Saving', autoPromote, autoDelete)
      if (name.length) {
        onUpdateProject(
          name,
          description,
          project.area,
          autoPromote,
          autoDelete
        )
      }
      setEditName(false)
      setEditDescription(false)
      onEditMode(false)
    }, [
      autoDelete,
      autoPromote,
      description,
      name,
      onEditMode,
      onUpdateProject,
      project.area
    ])

    const handleKeyPress = event => {
      if (event.repeat) {
        return
      }
      if (event.keyCode === 13) {
        handleSave()
      }
    }

    const handleAreaChange = useCallback(
      areaId => {
        onUpdateProject(name, description, areaId, autoPromote, autoDelete)
      },
      [autoDelete, autoPromote, description, name, onUpdateProject]
    )

    const handleAutoPromoteChange = useCallback(
      checked => {
        setAutoPromote(checked)
        onUpdateProject(name, description, project.area, checked, autoDelete)
      },
      [autoDelete, description, name, onUpdateProject, project.area]
    )

    const handleAutoDeleteChange = useCallback(
      checked => {
        setAutoDelete(checked)
        onUpdateProject(name, description, project.area, autoPromote, checked)
      },
      [autoPromote, description, name, onUpdateProject, project.area]
    )

    const nameFragment = (
      <Fragment>
        {!editName && (
          <Typography
            variant='h5'
            onClick={e => {
              setEditName(true)
              setEditDescription(false)
              onEditMode(true)
              e.stopPropagation()
            }}
            onFocus={e => e.stopPropagation()}
            display='inline'
          >
            {name}
          </Typography>
        )}
        {editName && (
          <Fragment>
            <ClickAwayListener onClickAway={handleSave}>
              <Input
                placeholder='Project Name'
                variant='contained'
                value={name}
                inputRef={input => input && input.focus()}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => handleKeyPress(e)}
                onClick={e => e.stopPropagation()}
                onFocus={e => e.stopPropagation()}
                fullWidth={true}
                inputProps={{
                  'aria-label': 'project-name',
                  'data-lpignore': 'true',
                  maxLength: MAX_LENGTH_PROJECT_NAME
                }}
              />
            </ClickAwayListener>
            <br />
            <br />
          </Fragment>
        )}
      </Fragment>
    )

    const descriptionFragment = (
      <Fragment>
        {!editDescription && (
          <Typography
            className={classes.description}
            color='textSecondary'
            variant='body2'
            onClick={e => {
              setEditName(false)
              setEditDescription(true)
              onEditMode(true)
            }}
          >
            {description}
          </Typography>
        )}
        {editDescription && 'abc' && (
          <ClickAwayListener onClickAway={handleSave}>
            <TextField
              multiline
              rows={4}
              rowsMax={15}
              placeholder='Details...'
              value={description}
              variant='outlined'
              onChange={e => setDescription(e.target.value)}
              fullWidth={true}
              inputProps={{
                'aria-label': 'project-description',
                'data-lpignore': 'true',
                maxLength: MAX_LENGTH_PROJECT_DESCRIPTION
              }}
            />
          </ClickAwayListener>
        )}
      </Fragment>
    )

    const detailsFragment = (
      <Fragment>
        <Grid container spacing={2}>
          <Grid
            item
            xs={6}
            sm={6}
            md={8}
            style={{
              borderRightStyle: 'solid',
              borderRightWidth: 'thin',
              borderRightColor: theme.palette.divider
            }}
          >
            {descriptionFragment}
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <AreaSelect
                  decrypt={decrypt}
                  getAreas={getAreas}
                  area={project.area}
                  showLabel={true}
                  onSetArea={handleAreaChange}
                  classes={{
                    areaSelect: classes.fullWidth
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  style={{ marginTop: theme.spacing(1) }}
                  color='secondary'
                  variant='contained'
                  onClick={e => {
                    setOpenConfirmDeleteProject(true)
                    e.stopPropagation()
                  }}
                  onFocus={e => e.stopPropagation()}
                >
                  Delete Project
                </Button>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  className={classes.checkbox}
                  control={
                    <Checkbox
                      className={classes.checkbox}
                      checked={autoPromote}
                      onChange={useCallback(
                        event => handleAutoPromoteChange(event.target.checked),
                        [handleAutoPromoteChange]
                      )}
                      name='Auto Promote'
                      color='primary'
                    />
                  }
                  label='Auto Promote'
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  className={classes.checkbox}
                  control={
                    <Checkbox
                      className={classes.checkbox}
                      checked={autoDelete}
                      onChange={useCallback(
                        event => handleAutoDeleteChange(event.target.checked),
                        [handleAutoDeleteChange]
                      )}
                      name='Auto Delete'
                      color='primary'
                    />
                  }
                  label='Auto Delete'
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Fragment>
    )

    return (
      <Fragment>
        <Hidden smDown>
          <Card>
            <CardContent>
              {nameFragment}
              <Box style={{ display: 'flex' }}>{detailsFragment}</Box>
            </CardContent>
          </Card>
        </Hidden>
        <Hidden mdUp>
          <Accordion className={classes.accordion} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {nameFragment}
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              {detailsFragment}
            </AccordionDetails>
          </Accordion>
        </Hidden>
        <ConfirmDelete
          open={openConfirmDeleteProject}
          deleteItem={name}
          onOk={useCallback(async () => {
            await onDeleteProject()
            setOpenConfirmDeleteProject(false)
          }, [onDeleteProject])}
          onCancel={useCallback(() => setOpenConfirmDeleteProject(false), [])}
        />
      </Fragment>
    )
  }
)
